// 5단계 화면 틀 (docs/ux/draft-v3): 첫 화면(방 고르기), 위 바(나가기·방 이름·Leaf), 알림, 아래 바(Go to · Actions · Talk · Grooming),
// 아래 바 위에 붙는 패널(장소 버튼·키보드·메뉴), 아래 바를 덮는 큰 시트(Grooming·Leaf 상점), 상황 버튼.
// 화면을 최대한 가리지 않게 작고 반투명하게. 3D 쪽 동작은 main.ts가 콜백으로 넘긴다.
import { LINE } from './lineIcons';
import { HUD_TEXT as TX, fill } from '../text/en';

export type Tab = 'goto' | 'actions' | 'talk' | 'grooming';
export interface RoomCard { key: string; name: string; blurb: string; count: string; tone: string }
export interface Ctx { label: string; icon: keyof typeof LINE; click(): void }

export interface Shell {
  /** 첫 화면을 보이거나 숨긴다. */
  home(cards: RoomCard[] | null): void;
  readonly inHome: boolean;
  setRoom(name: string): void;
  setLeaf(n: number): void;
  toast(msg: string, ms?: number): void;
  readonly tab: Tab | null;
  setTab(t: Tab | null): void;
  /** 온천: Talk 대신 자물쇠 달린 "Quiet". */
  setTalkLocked(on: boolean): void;
  /** 아래 바 위에 붙는 패널 (null이면 닫기). */
  dock(el: HTMLElement | null): void;
  /** 아래 바까지 덮는 큰 시트 (null이면 닫기). */
  sheet(el: HTMLElement | null): void;
  setCtx(c: Ctx | null): void;
  /** 화면 아래를 가린 높이(px): 카메라를 그만큼 올려 내 카피바라가 보이게. */
  covered(): number;
  /** 원형 동작 메뉴 등 3D 위에 띄우는 층. */
  readonly layer: HTMLElement;
}

export const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, html?: string): HTMLElementTagNameMap[K] => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html != null) e.innerHTML = html;
  return e;
};
export function iconBtn(cls: string, icon: string, label?: string, aria?: string): HTMLButtonElement {
  const b = el('button', cls, icon);
  b.type = 'button';
  if (label) b.append(el('span', '', label));
  if (aria) b.setAttribute('aria-label', aria);
  return b;
}

export function createShell(host: HTMLElement, h: { onExit(): void; onLeaf(): void; onTab(t: Tab): void; onPick(key: string): void }): Shell {
  const root = el('div', 'hud');
  host.append(root);
  const layer = el('div', 'hud-layer');
  // 위 바
  const top = el('div', 'hud-top');
  const exit = iconBtn('round glass', LINE.exit, undefined, TX.exit);
  const roomName = el('div', 'pill glass');
  const leafBtn = el('button', 'pill glass leafbtn');
  leafBtn.type = 'button';
  const leafN = el('b', '', '0');
  leafBtn.append(el('span', 'leaf', LINE.leaf), leafN, el('span', 'plus', LINE.plus));
  const tl = el('div', 'tl'); tl.append(exit, roomName);
  top.append(tl, leafBtn);
  exit.addEventListener('click', h.onExit);
  leafBtn.addEventListener('click', h.onLeaf);
  // 알림
  const toastEl = el('div', 'toast glass');
  toastEl.setAttribute('role', 'status');
  let toastTimer = 0;
  // 아래 바
  const nav = el('nav', 'hud-nav glass');
  nav.setAttribute('aria-label', 'Main');
  const tabs: Record<Tab, HTMLButtonElement> = {
    goto: iconBtn('', LINE.goto, TX.nav.goto), actions: iconBtn('', LINE.actions, TX.nav.actions),
    talk: iconBtn('', LINE.talk, TX.nav.talk), grooming: iconBtn('', LINE.grooming, TX.nav.grooming),
  };
  (Object.keys(tabs) as Tab[]).forEach((k) => { nav.append(tabs[k]); tabs[k].addEventListener('click', () => h.onTab(k)); });
  const dockEl = el('div', 'hud-dock');
  const sheetEl = el('div', 'hud-sheet');
  const ctxBtn = el('button', 'ctx');
  ctxBtn.type = 'button';
  let ctxClick: (() => void) | null = null, ctxKey = '';
  ctxBtn.addEventListener('click', () => ctxClick?.());
  // 첫 화면
  const homeEl = el('div', 'home');
  root.append(layer, top, toastEl, ctxBtn, dockEl, sheetEl, nav, homeEl);

  let tab: Tab | null = null, inHome = false;
  const S: Shell = {
    get inHome() { return inHome; },
    get tab() { return tab; },
    layer,
    home(cards) {
      inHome = !!cards;
      root.classList.toggle('at-home', inHome);
      homeEl.replaceChildren();
      if (!cards) return;
      homeEl.append(el('h1', '', TX.title), el('p', 'sub', TX.sub));
      const list = el('div', 'cards');
      for (const c of cards) {
        const b = el('button', 'card glass');
        b.type = 'button';
        b.append(el('i', 'th', ''), el('div', 'txt', `<b>${c.name}</b><small>${c.blurb}</small>`), el('em', '', `${c.count}<br><span>${TX.inside}</span>`));
        (b.firstChild as HTMLElement).style.background = c.tone;
        b.addEventListener('click', () => h.onPick(c.key));
        list.append(b);
      }
      homeEl.append(list);
    },
    setRoom(name) { roomName.textContent = name; },
    setLeaf(n) { leafN.textContent = String(n); leafBtn.setAttribute('aria-label', fill(TX.leaf, { n })); },
    toast(msg, ms = 3200) {
      if (!msg) return;
      toastEl.textContent = msg; toastEl.classList.add('on');
      clearTimeout(toastTimer); toastTimer = window.setTimeout(() => toastEl.classList.remove('on'), ms);
    },
    setTab(t) {
      tab = t;
      (Object.keys(tabs) as Tab[]).forEach((k) => tabs[k].classList.toggle('on', k === t));
    },
    setTalkLocked(on) {
      tabs.talk.replaceChildren();
      tabs.talk.insertAdjacentHTML('beforeend', on ? LINE.lock : LINE.talk);
      tabs.talk.append(el('span', '', on ? TX.nav.quiet : TX.nav.talk));
      tabs.talk.classList.toggle('off', on);
    },
    dock(e) { dockEl.replaceChildren(); if (e) dockEl.append(e); root.classList.toggle('docked', !!e); },
    sheet(e) { sheetEl.replaceChildren(); if (e) sheetEl.append(e); root.classList.toggle('sheeted', !!e); },
    setCtx(c) {
      const key = c ? c.icon + c.label : '';
      ctxClick = c ? c.click : null;
      if (key === ctxKey) return;
      ctxKey = key;
      ctxBtn.replaceChildren();
      if (c) { ctxBtn.insertAdjacentHTML('beforeend', LINE[c.icon]); ctxBtn.append(el('span', '', c.label)); }
      ctxBtn.classList.toggle('on', !!c);
    },
    covered() {
      if (inHome) return 0;
      const hEl = root.classList.contains('sheeted') ? sheetEl : root.classList.contains('docked') ? dockEl : null;
      if (!hEl) return 0;
      const r = hEl.getBoundingClientRect(), H = host.clientHeight;
      return Math.max(0, H - r.top - 88);
    },
  };
  return S;
}
